import {
    createDefaultOrganizationSystemSettings,
    OrganizationSettingsSystemSettingsType,
} from './../../../../../../../types/src/organization/types/organization-settings/organization-settings-system-settings.type';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { selectCurrentActiveIntegration } from 'packages/client/src/app/store/global/global.selectors';
import { ComponentStoreV2Factory } from '../../../shared/store/common/component-store-v2.factory';
import { globalStore } from '../../../shared/shared.global-service';
import { FilterBuilder } from '../../../content/helper/filter.builder';
import { notificationIdFrag } from '../../services/types/notification.types';
import { ExportImportProgressObject } from '@libs/types/src/export-import/object-type/export-import.progress-object';
import { PROGRESS_CONNECTION_LOST } from '../../../shared/apollo-links/websocket-connection';
import { BackgroundTaskStore } from '../../store/background-tasks/background-task.store';

@Injectable()
export class TopbarStore extends ComponentStoreV2Factory {

    public notificationsCount = signal<number>(0);
    public readonly backgroundTaskStore = inject(BackgroundTaskStore);
    public readonly totalAttentionCount = computed(() =>
        this.notificationsCount() + this.backgroundTaskStore.unreadCount(),
    );

    systemSettings = computed(() => {
        const systemSettings: any = globalStore.selectSignal(selectCurrentActiveIntegration(OrganizationSettingTypeEnum.SYSTEM_SETTINGS))();

        return systemSettings?.data
            ? (JSON.parse(systemSettings.data) as OrganizationSettingsSystemSettingsType)
            : createDefaultOrganizationSystemSettings();
    });

    public progressData = signal<Map<string, ExportImportProgressObject>>(new Map<string, ExportImportProgressObject>());

    constructor() {
        super();

        effect(() => {
            const notification = this.globalGlobalStore.loggedUserStore.newNotification();
            notification && this.notificationsCount.update(prev => prev + 1);
        });

        effect(() => {
            const progressItem = this.globalGlobalStore.loggedUserStore.exportImportProgress();
            if (!progressItem) return;

            if (progressItem.progressId === PROGRESS_CONNECTION_LOST) {
                this.clearProgresses();
            } else {
                this.updateProgress(progressItem);
            }
        });

        effect(() => {
            const successReadOrDelete =
                this.globalGlobalStore.apiSuccess()?.key === 'readNotification' ||
                this.globalGlobalStore.apiSuccess()?.key === 'readAllNotifications' ||
                this.globalGlobalStore.apiSuccess()?.key === 'deleteNotification' ||
                this.globalGlobalStore.apiSuccess()?.key === 'deleteAllNotifications'
                    ? this.globalGlobalStore.apiSuccess()?.data
                    : null;

            if (successReadOrDelete !== null) {
                this.setUnreadNotificationsCount();
            }
        });

        this.setUnreadNotificationsCount();
    }

    private setUnreadNotificationsCount() {
        const user = this.globalGlobalStore.loggedUserStore.currentUser();
        if (!user) return;

        this.api.getNotifications(FilterBuilder.input({
            userId: user.id,
            isRead: false,
            sort: {
                column: 'createdAt',
                direction: 'DESC',
            },
        }).input, notificationIdFrag).then((not) => {
            this.notificationsCount.set(not?.['paging'].total);
        });
    }

    private updateProgress(progressItem: ExportImportProgressObject): void {
        this.progressData.update(map => {
            const updatedMap = new Map(map);

            if (progressItem.finished) {
                updatedMap.delete(progressItem.progressId);
                if (progressItem.errors.length) {
                    console.error("Progress errs:", progressItem.name, progressItem.errors);
                }
            } else {
                updatedMap.set(progressItem.progressId, progressItem);
            }

            return updatedMap;
        });
    }

    private clearProgresses(): void {
        this.progressData.set(new Map());
    }
}
